import { Reducer } from 'redux';
import moment from 'moment';
import { IUser } from '../../models/iuser';
import { updateObject } from '../../utils/updateObject';
import * as actionTypes from '../actions/actionTypes';
import { IScheduledService } from '../../models/ischeduledService';
import { IAutocompleteOption } from '../../models/iautocompleteOption';
import { ICompany } from '../../models/icompany';
import { IArea } from '../../models/iarea';
import { IServiceType } from '../../models/iserviceType';
import { IFrequency } from '../../models/ifrequency';
import { IEquipmentModel } from '../../models/iequipmentModel';
import { IEquipmentGroup } from '../../models/iequipmentGroup';
import { IPlace } from '../../models/iplace';
import { i18n } from '../../translate/i18n';
import { IEquipment } from '../../models/iequipment';
import { EServiceStatus, IEquipmentOption } from '../../containers/ScheduledServices/ScheduledServicesPlanner/Modals/ServicesForSchedulingFiltersModal';

export enum EFilterType {
  companies = 'companies',
  areas = 'areas',
  serviceTypes = 'serviceTypes',
  frequencies = 'frequencies',
  equipmentModels = 'equipmentModels',
  equipmentGroups = 'equipmentGroups',
  places = 'places',
  users = 'users',
}

export type IEquipmentServiceTobeScheduled = {
  _id: string,
  equipmentModel: {
    _id: string,
    name: string,
  },
  place: {
    _id: string,
    path: string,
  },
  site: string,
  scheduledServicesReplicated: {
    equipmentServiceId: {
      _id: string,
      frequency: {
        name: string,
        _id: string,
        onDemand: boolean,
      },
      serviceType: {
        name: string,
        _id: string,
      }
    },
    equipmentServiceName: string,
    expirationDate: Date,
  }
  serialNumber: string,
  photo?: {
    key: string,
    url: string,
  },
  equipmentServicesReplicate: {
    equipmentServiceId: string,
    expirationDate: Date,
  }[],
  companies: {
    _id: string,
    name: string,
  }[],
};

export interface ICountByUserAndDay {
  _id: string,
  dates: { day: string, totalCompleted: number, totalNotCompleted: number }[],
}

export interface IAction<T> {
  type: string;
  payload: T;
}

export interface IFilter {
  label: string
  value: string
}

export interface IScheduledServiceCalendarEventResponse {
  day: number,
  month: number,
  year: number,
  totalEvents: number,
  completed: boolean,
}

export interface IScheduledServiceCalendarEvent {
  title: string,
  quantity: number,
  start: Date,
  end: Date,
  eventColor: string,
  borderColor: string,
  completed: boolean,
}

export interface IScheduledServiceState {
  servicesCountByUsersAndDays: ICountByUserAndDay [],
  servicesByUserAndDay: IScheduledService[],
  servicesForScheduling: IEquipmentServiceTobeScheduled[],
  selectedSiteId: string,
  users: IUser[],
  loadingUsers: boolean,
  loadingServicesForScheduling: boolean,
  loadingMoreServicesForScheduling: boolean,
  loadingPostScheduledService: boolean,
  loadingCountByUsersAndDays: boolean,
  loadingGetByUserAndDay: boolean,
  loadingSoftDeleteService: boolean,
  openModal: boolean,
  showCompletedOnly: boolean,
  selectedUserId: string,
  selectedDate: string,
  filtersUsers: {
    roles: IAutocompleteOption[],
    companies: IAutocompleteOption[],
  },
  filtersServicesForScheduling: {
    companies: IFilter[],
    areas: IFilter[],
    serviceTypes: IFilter[],
    frequencies: IFilter[],
    equipmentModels: IFilter[],
    equipmentGroups: IFilter[],
    places: IFilter[],
    startDate: null | Date,
    endDate: null | Date,
    equipment: IEquipmentOption|null,
    serviceStatus: EServiceStatus[],
  },
  companies: ICompany[],
  areas: IArea[],
  serviceTypes: IServiceType[],
  frequencies: IFrequency[],
  equipmentModels: IEquipmentModel[],
  equipmentGroups: IEquipmentGroup[],
  equipments: IEquipment[],
  places: IPlace[],
  placesForCalendar: IPlace[],
  usersForFilters: IUser[],
  loadingEquipments: boolean,
  loadingEquipmentModels: boolean,
  loadingEquipmentGroups: boolean,
  loadingAreas: boolean,
  loadingCompanies: boolean,
  loadingPlaces: boolean,
  loadingFrequencies: boolean,
  loadingServiceTypes: boolean,
  loadingUsersForFilters: boolean,
  count: number,
  calendar: {
    filters: {
      companies: IFilter[],
      areas: IFilter[],
      serviceTypes: IFilter[],
      frequencies: IFilter[],
      equipmentModels: IFilter[],
      equipmentGroups: IFilter[],
      places: IFilter[],
      users: IFilter[],
    },
    loadingCalendarEvents: boolean,
    calendarEvents: IScheduledServiceCalendarEvent[],
    selectedEvent: {
      date: string,
      completed: boolean,
      borderColor: string,
      title: string,
    },
    servicesByDayAndCompleted: IScheduledService[],
    loadingServicesByDayAndCompleted: boolean,
  },
  showSelectManyCheckBoxes: boolean,
  selectedServicesToBeScheduled: IEquipmentServiceTobeScheduled[],
  loadingModalUsers: boolean,
  modalUsers: IUser[],
  loadingModalPostUpdateScheduledService: boolean,
  loadingCalendarReportExcell: boolean,
}

const initialState: IScheduledServiceState = {
  servicesCountByUsersAndDays: [],
  servicesByUserAndDay: [],
  servicesForScheduling: [],
  selectedSiteId: "",
  users: [],
  loadingUsers: false,
  loadingServicesForScheduling: false,
  loadingMoreServicesForScheduling: false,
  loadingPostScheduledService: false,
  loadingCountByUsersAndDays: false,
  loadingGetByUserAndDay: false,
  loadingSoftDeleteService: false,
  openModal: false,
  showCompletedOnly: false,
  selectedUserId: "",
  selectedDate: "",
  filtersUsers: {
    roles: [],
    companies: [],
  },
  filtersServicesForScheduling: {
    companies: [],
    areas: [],
    serviceTypes: [],
    frequencies: [],
    equipmentModels: [],
    equipmentGroups: [],
    places: [],
    serviceStatus: [],
    startDate: null,
    endDate: null,
    equipment: null,
  },
  companies: [],
  areas: [],
  serviceTypes: [],
  frequencies: [],
  equipmentModels: [],
  equipmentGroups: [],
  places: [],
  equipments: [],
  placesForCalendar: [],
  usersForFilters: [],
  loadingEquipments: false,
  loadingEquipmentModels: false,
  loadingEquipmentGroups: false,
  loadingAreas: false,
  loadingCompanies: false,
  loadingPlaces: false,
  loadingFrequencies: false,
  loadingServiceTypes: false,
  loadingUsersForFilters: false,
  count: 0,
  calendar: {
    filters: {
      companies: [],
      areas: [],
      serviceTypes: [],
      frequencies: [],
      equipmentModels: [],
      equipmentGroups: [],
      places: [],
      users: [],
    },
    loadingCalendarEvents: false,
    calendarEvents: [],
    selectedEvent: {
      date: "",
      completed: false,
      borderColor: "",
      title: "",
    },
    servicesByDayAndCompleted: [],
    loadingServicesByDayAndCompleted: false,
  },
  showSelectManyCheckBoxes: false,
  selectedServicesToBeScheduled: [],
  loadingModalUsers: false,
  modalUsers: [],
  loadingModalPostUpdateScheduledService: false,
  loadingCalendarReportExcell: false,
}

const fetchUsersStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingUsers: true });
}

const fetchUsersSuccess = (state: IScheduledServiceState, action: IAction<IUser[]>) => {
  return updateObject(state, { loadingUsers: false, users: action.payload });
}

const fetchUsersFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingUsers: false });
}

const fetchServicesForSchedulingStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingServicesForScheduling: true });
}

const fetchServicesForSchedulingSuccess = (state: IScheduledServiceState, action: IAction<{rows: IEquipmentServiceTobeScheduled[], count: number}>) => {
  return updateObject(state, { loadingServicesForScheduling: false, servicesForScheduling: action.payload.rows, count: action.payload.count });
}

const fetchServicesForSchedulingFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingServicesForScheduling: false });
}

const fetchMoreServicesForSchedulingStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingMoreServicesForScheduling: true });
}

const fetchMoreServicesForSchedulingFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingMoreServicesForScheduling: false });
}

const fetchMoreServicesForSchedulingSuccess = (state: IScheduledServiceState, action: IAction<{rows: IEquipmentServiceTobeScheduled[], count: number}>) => {
  return updateObject(state, { loadingMoreServicesForScheduling: false, servicesForScheduling: [...state.servicesForScheduling, ...action.payload.rows], count: action.payload.count });
}

const postScheduledServiceStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingPostScheduledService: true });
}

const postScheduledServiceSuccessFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingPostScheduledService: false });
}

const countByUsersAndDaysStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingCountByUsersAndDays: true });
}

const countByUsersAndDaysSuccess = (state: IScheduledServiceState, action: IAction<ICountByUserAndDay[]>) => {
  return updateObject(state, { loadingCountByUsersAndDays: false, servicesCountByUsersAndDays: action.payload });
}

const countByUsersAndDaysFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingCountByUsersAndDays: false });
}

const fetchByUserAndDayStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingGetByUserAndDay: true });
}

const fetchByUserAndDaySuccess = (state: IScheduledServiceState, action: IAction<IScheduledService[]>) => {
  return updateObject(state, { loadingGetByUserAndDay: false, servicesByUserAndDay: action.payload });
}

const fetchByUserAndDayFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingGetByUserAndDay: false });
}

const openCloseModal = (state: IScheduledServiceState, action: IAction<boolean>) => {
  return updateObject(state, { openModal: !state.openModal, showCompletedOnly: action.payload });
}

const selectUserAndDate = (state: IScheduledServiceState, action: IAction<{ userId: string, date: string }>) => {
  return updateObject(state, { selectedUserId: action.payload.userId, selectedDate: action.payload.date });
}

const softDeleteScheduledServiceStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingSoftDeleteService: true });
}

const softDeleteScheduledServiceSuccessFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingSoftDeleteService: false });
}

const setUsersFilters = (state: IScheduledServiceState, action: IAction<IScheduledServiceState['filtersUsers']>) => {
  return updateObject(state, { filtersUsers: action.payload });
}

const clearUsersFilters = (state: IScheduledServiceState) => {
  return updateObject(state, { filtersUsers: initialState.filtersUsers });
}

const setServicesForSchedulingFilters = (state: IScheduledServiceState, action: IAction<IScheduledServiceState['filtersServicesForScheduling']>) => {
  return updateObject(state, { filtersServicesForScheduling: action.payload })
}

const clearServicesForSchedulingFilters = (state: IScheduledServiceState) => {
  return updateObject(state, { filtersServicesForScheduling: initialState.filtersServicesForScheduling  });
}

const fetchEquipmentModelsStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingEquipmentModels: true });
}

const fetchEquipmentModelsSuccess = (state: IScheduledServiceState, action: IAction<IEquipmentModel[]>) => {
  return updateObject(state, { loadingEquipmentModels: false, equipmentModels: action.payload });
}

const fetchEquipmentModelsFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingEquipmentModels: false });
}

const fetchEquipmentsStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingEquipments: true });
}

const fetchEquipmentsSuccess = (state: IScheduledServiceState, action: IAction<IEquipment[]>) => {
  return updateObject(state, { loadingEquipments: false, equipments: action.payload });
}

const fetchEquipmentsFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingEquipments: false });
}

const fetchGroupsStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingEquipmentGroups: true });
}

const fetchGroupsSuccess = (state: IScheduledServiceState, action: IAction<IEquipmentGroup[]>) => {
  return updateObject(state, { loadingEquipmentGroups: false, equipmentGroups: action.payload });
}

const fetchGroupsFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingEquipmentGroups: false });
}

const fetchAreasStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingAreas: true });
}

const fetchAreasSuccess = (state: IScheduledServiceState, action: IAction<IArea[]>) => {
  return updateObject(state, { loadingAreas: false, areas: action.payload });
}

const fetchAreasFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingAreas: false });
}

const fetchPlacesStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { loadingPlaces: true });
}

const fetchPlacesSuccess = (state: IScheduledServiceState, action: IAction<IPlace[]>) => {
  return updateObject(state, { loadingPlaces: false, places: action.payload });
}

const fetchPlacesFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingPlaces: false });
}

const fetchCompaniesStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { loadingCompanies: true });
}

const fetchCompaniesSuccess = (state: IScheduledServiceState, action: IAction<ICompany[]>) => {
  return updateObject(state, { loadingCompanies: false, companies: action.payload });
}

const fetchCompaniesFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingCompanies: false });
}

const fetchFrequenciesStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { loadingFrequencies: true });
}

const fetchFrequenciesSuccess = (state: IScheduledServiceState, action: IAction<IFrequency[]>) => {
  return updateObject(state, { loadingFrequencies: false, frequencies: action.payload });
}

const fetchFrequenciesFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingFrequencies: false });
}

const fetchServiceTypesStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { loadingServiceTypes: true });
}

const fetchServiceTypesSuccess = (state: IScheduledServiceState, action: IAction<IServiceType[]>) => {
  return updateObject(state, { loadingServiceTypes: false, serviceTypes: action.payload });
}

const fetchServiceTypesFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingServiceTypes: false });
}

const selectSite = (state:IScheduledServiceState, action: IAction<string>) => {
  return updateObject(state, { selectedSiteId: action.payload });
}

// CALENDAR

const clearCalendarFilters = (state:IScheduledServiceState) => {
  return updateObject(state, { calendar: { ...state.calendar, filters: initialState.calendar.filters } })
}

const clearCalendarSelectedFilter = (state: IScheduledServiceState, action: IAction<{ type: EFilterType, id: string}>) => {
  const selectedFilters = state.calendar.filters[action.payload.type];
  const updatedFilters = selectedFilters.filter(filter => filter.value !== action.payload.id)
  return updateObject(state, { calendar: { ...state.calendar, filters: { ...state.calendar.filters, [action.payload.type]: updatedFilters } } })
}

const setCalendarFilters = (state: IScheduledServiceState, action: IAction<IScheduledServiceState['calendar']['filters']>) => {
  return updateObject(state, { calendar: { ...state.calendar, filters: action.payload } })
}

const fetchUsersForFiltersStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { loadingUsersForFilters: true });
}

const fetchUsersForFiltersSuccess = (state: IScheduledServiceState, action: IAction<IUser[]>) => {
  return updateObject(state, { loadingUsersForFilters: false, usersForFilters: action.payload });
}

const fetchUsersForFiltersFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingUsersForFilters: false });
}

const fetchPlacesCalendarStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { loadingPlaces: true });
}

const fetchPlacesCalendarSuccess = (state: IScheduledServiceState, action: IAction<IPlace[]>) => {
  return updateObject(state, { loadingPlaces: false, placesForCalendar: action.payload });
}

const fetchPlacesCalendarFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingPlaces: false });
}

const fetchCalendarEventsStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { calendar: { ...state.calendar, loadingCalendarEvents: true } });
}

const fetchCalendarEventsSuccess = (state: IScheduledServiceState, action: IAction<IScheduledServiceCalendarEventResponse[]>) => {
  const calendarEvents = action.payload.map((event) => {
    const date = new Date(event.year, event.month - 1, event.day);
    let borderColor = '#1B5E20';
    let eventColor = '#2E7D324D';
    let title = i18n.t('reducerScheduledServices.completed');

    if (!event.completed) {
      if (moment(date).isBefore(new Date(), 'day')) {
        borderColor = '#C62828';
        eventColor = '#D32F2F4D';
        title = i18n.t('reducerScheduledServices.delayed');
      } else {
        borderColor = '#0288D1';
        eventColor = '#0288D14D';
        title = i18n.t('reducerScheduledServices.scheduledServices');
      }
    }

    return {
      title,
      quantity: event.totalEvents,
      start: date,
      end: date,
      borderColor,
      eventColor,
      completed: event.completed,
    }
  })
  return updateObject(state, { calendar: { ...state.calendar, loadingCalendarEvents: false, calendarEvents } });
}

const fetchCalendarEventsFail = (state: IScheduledServiceState) => {
  return updateObject(state, { calendar: { ...state.calendar, loadingCalendarEvents: false } });
}

const selectCalendarEvent = (state: IScheduledServiceState, action: IAction<{date: string, completed: boolean, borderColor: string, title: string}>) => {
  return updateObject(state, { calendar: { ...state.calendar, selectedEvent: action.payload } });
}

const fetchServicesByDayAndCompletedStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { calendar: { ...state.calendar, loadingServicesByDayAndCompleted: true } });
}

const fetchServicesByDayAndCompletedSuccess = (state: IScheduledServiceState, action: IAction<IScheduledService[]>) => {
  return updateObject(state, { calendar: { ...state.calendar, loadingServicesByDayAndCompleted: false, servicesByDayAndCompleted: action.payload } });
}

const fetchServicesByDayAndCompletedFail = (state: IScheduledServiceState) => {
  return updateObject(state, { calendar: { ...state.calendar, loadingServicesByDayAndCompleted: false } });
}

const toggleShowSelectManyCheckBoxes = (state: IScheduledServiceState) => {
  return updateObject(state, { showSelectManyCheckBoxes: !state.showSelectManyCheckBoxes});
}

const addServiceToBeScheduled = (state: IScheduledServiceState, action: IAction<IEquipmentServiceTobeScheduled>) => {
  const equipmentWithService = [...state.selectedServicesToBeScheduled, action.payload];
  return updateObject(state, { selectedServicesToBeScheduled: equipmentWithService});
}

const removeServiceToBeScheduled = (state: IScheduledServiceState, action: IAction<IEquipmentServiceTobeScheduled>) => {
  const {_id, scheduledServicesReplicated: { equipmentServiceId: { _id: equipServiceId } }} = action.payload;
  
  const equipmentWithService = [...state.selectedServicesToBeScheduled].filter((equipment) => !(equipment._id === _id && equipment.scheduledServicesReplicated.equipmentServiceId._id === equipServiceId));
  
  return updateObject(state, { selectedServicesToBeScheduled: equipmentWithService});
}

const clearServicesToBeScheduled = (state: IScheduledServiceState) => {
  return updateObject(state, { selectedServicesToBeScheduled: []});
}

const addAllServicesToBeScheduled = (state: IScheduledServiceState) => {
  return updateObject(state, { selectedServicesToBeScheduled: state.servicesForScheduling});
}

const fetchCalendarReportStart = (state: IScheduledServiceState): IScheduledServiceState => {
  return updateObject(state, { loadingCalendarReportExcell: true });
}

const fetchCalendarReportSuccess = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingCalendarReportExcell: false });
}

const fetchCalendarReportFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingCalendarReportExcell: false });
}

// MODAL SCHEDULE SERVICE

const fetchModalUsersStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingModalUsers: true});
}

const fetchModalUsersSuccess = (state: IScheduledServiceState, action: IAction<IUser[]>) => {
  return updateObject(state, { loadingModalUsers: false, modalUsers: action.payload });
}

const fetchModalUsersFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingModalUsers: false});
}

const postScheduledServiceModalStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingModalPostUpdateScheduledService: true});
}

const postScheduledServiceModalSuccess = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingModalPostUpdateScheduledService: false});
}

const postScheduledServiceModalFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingModalPostUpdateScheduledService: false});
}

const updateScheduledServiceModalStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingModalPostUpdateScheduledService: true});
}

const updateScheduledServiceModalSuccess = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingModalPostUpdateScheduledService: false});
}

const updateScheduledServiceModalFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingModalPostUpdateScheduledService: false});
}

const softDeleteCalendarScheduledServiceStart = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingSoftDeleteService: true });
}

const softDeleteCalendarScheduledServiceSuccessFail = (state: IScheduledServiceState) => {
  return updateObject(state, { loadingSoftDeleteService: false });
}

const scheduledServicesReducer: Reducer<IScheduledServiceState, IAction<any>> = (state = initialState, action: IAction<any>) => {
  switch(action.type) {
    //PLANNER
    case actionTypes.SCHEDULED_SERVICES_GET_USERS_START: return fetchUsersStart(state);
    case actionTypes.SCHEDULED_SERVICES_GET_USERS_SUCCESS: return fetchUsersSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_GET_USERS_FAIL: return fetchUsersFail(state);
    case actionTypes.SCHEDULED_SERVICES_GET_SERVICES_FOR_SCHEDULING_START: return fetchServicesForSchedulingStart(state);
    case actionTypes.SCHEDULED_SERVICES_GET_SERVICES_FOR_SCHEDULING_SUCCESS: return fetchServicesForSchedulingSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_GET_SERVICES_FOR_SCHEDULING_FAIL: return fetchServicesForSchedulingFail(state);
    case actionTypes.SCHEDULED_SERVICES_GET_MORE_SERVICES_FOR_SCHEDULING_START: return fetchMoreServicesForSchedulingStart(state);
    case actionTypes.SCHEDULED_SERVICES_GET_MORE_SERVICES_FOR_SCHEDULING_FAIL: return fetchMoreServicesForSchedulingFail(state);
    case actionTypes.SCHEDULED_SERVICES_GET_MORE_SERVICES_FOR_SCHEDULING_SUCCESS: return fetchMoreServicesForSchedulingSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_POST_START: return postScheduledServiceStart(state);
    case actionTypes.SCHEDULED_SERVICES_POST_SUCCESS: return postScheduledServiceSuccessFail(state);
    case actionTypes.SCHEDULED_SERVICES_POST_FAIL: return postScheduledServiceSuccessFail(state);
    case actionTypes.SCHEDULED_SERVICES_COUNT_BY_USERS_AND_DAYS_START: return countByUsersAndDaysStart(state);
    case actionTypes.SCHEDULED_SERVICES_COUNT_BY_USERS_AND_DAYS_SUCCESS: return countByUsersAndDaysSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_COUNT_BY_USERS_AND_DAYS_FAIL: return countByUsersAndDaysFail(state);
    case actionTypes.SCHEDULED_SERVICES_GET_BY_USER_AND_DAY_START: return fetchByUserAndDayStart(state);
    case actionTypes.SCHEDULED_SERVICES_GET_BY_USER_AND_DAY_SUCCESS: return fetchByUserAndDaySuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_GET_BY_USER_AND_DAY_FAIL: return fetchByUserAndDayFail(state);
    case actionTypes.SCHEDULED_SERVICES_OPEN_CLOSE_SERVICES_BY_USER_AND_DAY_MODAL: return openCloseModal(state, action);
    case actionTypes.SCHEDULED_SERVICES_SELECT_USER_AND_DATE: return selectUserAndDate(state, action);
    case actionTypes.SCHEDULED_SERVICES_SOFT_DELETE_START: return softDeleteScheduledServiceStart(state);
    case actionTypes.SCHEDULED_SERVICES_SOFT_DELETE_SUCCESS: return softDeleteScheduledServiceSuccessFail(state);
    case actionTypes.SCHEDULED_SERVICES_SOFT_DELETE_FAIL: return softDeleteScheduledServiceSuccessFail(state);
    case actionTypes.SCHEDULED_SERVICES_CLEAR_USERS_FILTERS: return clearUsersFilters(state);
    case actionTypes.SCHEDULED_SERVICES_SET_USERS_FILTERS: return setUsersFilters(state, action);
    case actionTypes.SCHEDULED_SERVICES_CLEAR_SERVICES_FOR_SCHEDULING_FILTERS: return clearServicesForSchedulingFilters(state);
    case actionTypes.SCHEDULED_SERVICES_SET_SERVICES_FOR_SCHEDULING_FILTERS: return setServicesForSchedulingFilters(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_PLACES_START: return fetchPlacesStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_PLACES_SUCCESS: return fetchPlacesSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_PLACES_FAIL: return fetchPlacesFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_EQUIPMENT_MODEL_START: return fetchEquipmentModelsStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_EQUIPMENT_MODEL_SUCCESS: return fetchEquipmentModelsSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_EQUIPMENT_MODEL_FAIL: return fetchEquipmentModelsFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_EQUIPMENTS_START: return fetchEquipmentsStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_EQUIPMENTS_SUCCESS: return fetchEquipmentsSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_EQUIPMENTS_FAIL: return fetchEquipmentsFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_GROUPS_START: return fetchGroupsStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_GROUPS_SUCCESS: return fetchGroupsSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_GROUPS_FAIL: return fetchGroupsFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_AREAS_START: return fetchAreasStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_AREAS_SUCCESS: return fetchAreasSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_AREAS_FAIL: return fetchAreasFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_COMPANIES_START: return fetchCompaniesStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_COMPANIES_SUCCESS: return fetchCompaniesSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_COMPANIES_FAIL: return fetchCompaniesFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_FREQUENCIES_START: return fetchFrequenciesStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_FREQUENCIES_SUCCESS: return fetchFrequenciesSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_FREQUENCIES_FAIL: return fetchFrequenciesFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_SERVICE_TYPES_START: return fetchServiceTypesStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_SERVICE_TYPES_SUCCESS: return fetchServiceTypesSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_SERVICE_TYPES_FAIL: return fetchServiceTypesFail(state);
    case actionTypes.SCHEDULED_SERVICES_SELECT_SITE: return selectSite(state, action);
    case actionTypes.SCHEDULED_SERVICES_TOGGLE_SHOW_SELECT_MANY_CHECKBOXES: return toggleShowSelectManyCheckBoxes(state);
    case actionTypes.SCHEDULED_SERVICES_ADD_SERVICE_TO_BE_SCHEDULED: return addServiceToBeScheduled(state, action);
    case actionTypes.SCHEDULED_SERVICES_REMOVE_SERVICE_TO_BE_SCHEDULED: return removeServiceToBeScheduled(state, action);
    case actionTypes.SCHEDULED_SERVICES_CLEAR_SERVICES_TO_BE_SCHEDULED: return clearServicesToBeScheduled(state);
    case actionTypes.SCHEDULED_SERVICES_ADD_ALL_SERVICES_TO_BE_SCHEDULED: return addAllServicesToBeScheduled(state);
    //CALENDAR
    case actionTypes.SCHEDULED_SERVICES_CLEAR_CALENDAR_FILTERS: return clearCalendarFilters(state);
    case actionTypes.SCHEDULED_SERVICES_CLEAR_CALENDAR_SELECTED_FILTER: return clearCalendarSelectedFilter(state, action);
    case actionTypes.SCHEDULED_SERVICES_SET_CALENDARS_FILTERS: return setCalendarFilters(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_USERS_START: return fetchUsersForFiltersStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_USERS_SUCCESS: return fetchUsersForFiltersSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_USERS_FAIL: return fetchUsersForFiltersFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_PLACES_CALENDAR_START: return fetchPlacesCalendarStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_PLACES_CALENDAR_SUCCESS: return fetchPlacesCalendarSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_PLACES_CALENDAR_FAIL: return fetchPlacesCalendarFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_CALENDAR_EVENTS_START: return fetchCalendarEventsStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_CALENDAR_EVENTS_SUCCESS: return fetchCalendarEventsSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_CALENDAR_EVENTS_FAIL: return fetchCalendarEventsFail(state);
    case actionTypes.SCHEDULED_SERVICES_CALENDAR_SELECT_EVENT: return selectCalendarEvent(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_SERVICES_BY_DAY_AND_COMPLETED_START: return fetchServicesByDayAndCompletedStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_SERVICES_BY_DAY_AND_COMPLETED_SUCCESS: return fetchServicesByDayAndCompletedSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_FETCH_SERVICES_BY_DAY_AND_COMPLETED_FAIL: return fetchServicesByDayAndCompletedFail(state);
    case actionTypes.SCHEDULED_SERVICES_CALENDAR_SOFT_DELETE_START: return softDeleteCalendarScheduledServiceStart(state);
    case actionTypes.SCHEDULED_SERVICES_CALENDAR_SOFT_DELETE_SUCCESS: return softDeleteCalendarScheduledServiceSuccessFail(state);
    case actionTypes.SCHEDULED_SERVICES_CALENDAR_SOFT_DELETE_FAIL: return softDeleteCalendarScheduledServiceSuccessFail(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_CALENDAR_REPORT_START: return fetchCalendarReportStart(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_CALENDAR_REPORT_SUCCESS: return fetchCalendarReportSuccess(state);
    case actionTypes.SCHEDULED_SERVICES_FETCH_CALENDAR_REPORT_FAIL: return fetchCalendarReportFail(state);

    // MODAL SCHEDULE SERVICE
    case actionTypes.SCHEDULED_SERVICES_MODAL_FETCH_USERS_START: return fetchModalUsersStart(state);
    case actionTypes.SCHEDULED_SERVICES_MODAL_FETCH_USERS_SUCCESS: return fetchModalUsersSuccess(state, action);
    case actionTypes.SCHEDULED_SERVICES_MODAL_FETCH_USERS_FAIL: return fetchModalUsersFail(state);
    case actionTypes.SCHEDULED_SERVICES_MODAL_POST_START: return postScheduledServiceModalStart(state);
    case actionTypes.SCHEDULED_SERVICES_MODAL_POST_SUCCESS: return postScheduledServiceModalSuccess(state);
    case actionTypes.SCHEDULED_SERVICES_MODAL_POST_FAIL: return postScheduledServiceModalFail(state);
    case actionTypes.SCHEDULED_SERVICES_MODAL_UPDATE_START: return updateScheduledServiceModalStart(state);
    case actionTypes.SCHEDULED_SERVICES_MODAL_UPDATE_SUCCESS: return updateScheduledServiceModalSuccess(state);
    case actionTypes.SCHEDULED_SERVICES_MODAL_UPDATE_FAIL: return updateScheduledServiceModalFail(state);
    
    default: return state;
  }
}

export default scheduledServicesReducer;
